import { useUnauthorizedInterceptor } from 'api/api';
import {
  useCheckIsValidUserProviderToken,
  useCurrentUser,
  useLogout,
} from 'api/hooks';
import { queryKeys } from 'api/keys';
import _ from 'lodash';
import { useNotifications } from 'notifications/hooks/useNotifications';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  getLoginProvider,
  getTokenData,
  removeTokenData,
  storeLoginProvider,
  storeTokenData,
  storeTokenLoading,
} from 'storage';
import { useHistory, useStorePreviousUrlBeforeLogin } from 'utils/hooks';
import echo from 'ws/echo';
import AuthContext from './AuthContext';

export default function AuthProvider({ children }) {
  const [isLoggedIn, setIsLoggedIn] = useState(getTokenData() != null);
  const { setNotifications } = useNotifications();
  const { prevUrl, setPrevUrl } = useStorePreviousUrlBeforeLogin(isLoggedIn);
  const history = useHistory();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const clearData = useCallback(() => {
    removeTokenData();
    setIsLoggedIn(false);
    storeTokenLoading(false);
    echo.disconnect();
    navigate('/login', {
      state: { flash: 'You have been logged out.' },
    });
  }, [navigate]);

  const setTokenData = useCallback(({ provider, ...tokenData }) => {
    storeTokenData(tokenData);
    storeLoginProvider(provider);
    setIsLoggedIn(getTokenData() != null);
    echo.setToken(tokenData.access_token);
    echo.connect();
  }, []);

  useUnauthorizedInterceptor(clearData, storeTokenData);

  const { logout: apiLogout } = useLogout({
    onSuccess: (data) => {
      clearData();
      // Invalidate search items cache
      queryClient.removeQueries([queryKeys.searchItems()[0]]);
      queryClient.removeQueries([queryKeys.user()[0]]);
      setNotifications([data?.message]);
      setPrevUrl('/');
    },
    onError: clearData,
  });

  const {
    user: apiUser,
    isLoading,
    refetch: getApiUser,
  } = useCurrentUser({
    enabled: isLoggedIn,
    onError: clearData,
  });

  useEffect(() => {
    if (isLoggedIn && !isLoading) {
      const token = getTokenData()?.access_token;
      if (token) {
        echo.setToken(token);
        echo.connect();
      }
    }
  }, [isLoggedIn, isLoading]);

  // prevent infinity loading, after page reload
  useEffect(() => {
    storeTokenLoading(false);
  }, []);

  const logout = useCallback(async () => {
    echo.disconnect();
    apiLogout();
  }, [apiLogout]);

  const canCheckProviderToken = !!(
    apiUser?.data &&
    isLoggedIn &&
    getLoginProvider() &&
    getLoginProvider() !== 'default'
  );

  useCheckIsValidUserProviderToken(getLoginProvider(), {
    // 600 - 10min
    refetchInterval: 600 * 1000,
    enabled: canCheckProviderToken,
    onError: clearData,
  });

  const auth = useMemo(
    () => ({
      user: apiUser?.data,
      isLoggedIn,
      setIsLoggedIn,
      setTokenData,
      logout,
      // display loader only first time
      isUserLoading: isLoading && _.isEmpty(apiUser?.data),
      refetchUser: getApiUser,
      prevUrl,
      setPrevUrl,
      clearData,
      history,
    }),
    [
      apiUser?.data,
      isLoggedIn,
      setIsLoggedIn,
      setTokenData,
      logout,
      isLoading,
      getApiUser,
      prevUrl,
      setPrevUrl,
      clearData,
      history,
    ],
  );

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
